/**
 * Redux slice that manages the user's geolocation state.
 *
 * The slice provides actions for setting the user location, handling errors,
 * setting loading state, and clearing errors. It also includes a thunk action creator
 * for retrieving the user's current position using the browser's Geolocation API.
 */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    coords: null,
    error: null,
    status: 'idle' // 'idle' | 'loading' | 'succeeded' | 'failed'
};

const useGeolocationSlice = createSlice({
    name: 'userGeolocation',
    initialState,
    reducers: {
        clearLocationError: (state) => {
            state.error = null;
            state.status = 'idle';
        },
        setUserLocation: (state, action) => {
            state.coords = action.payload;
            state.status = 'succeeded';
            // console.log("User Location: ", action.payload);
        },
        setLocationError: (state, action) => {
            state.error = action.payload;
            state.status = 'failed';
            // console.log("Error: ", action.payload);
        },
        setLoading: (state) => {
            state.status = 'loading';
        }
    }
});

export const { clearLocationError, setUserLocation, setLocationError, setLoading } =
    useGeolocationSlice.actions;

export default useGeolocationSlice.reducer;

/**
 * Action creator that retrieves the user's geolocation.
 *
 * This function uses the browser's Geolocation API to get the user's current position.
 * If geolocation is supported and the user gives permission, it dispatches the user's
 * latitude and longitude using the setUserLocation action.
 * If geolocation is not supported or the user denies permission, it dispatches an error
 * using the setLocationError action.
 *
 * @returns {Function} A thunk function that accepts dispatch as its argument
 * @example
 * dispatch(getUserLocation());
 */
export function getUserLocation() {
    return (dispatch) => {
        if (!navigator.geolocation) {
            console.table('Geolocation is not supported by your browser');
            dispatch(setLocationError('Geolocation is not supported by your browser'));
        } else {
            dispatch(setLoading());
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    dispatch(
                        setUserLocation({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        })
                    );
                    return position;
                },
                (error) => {
                    dispatch(setLocationError(error));
                    return null;
                }
            );
        }
    };
}
